import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../../components/Layout";
import { Tag, DatePicker, Select as AntSelect, Input, Card, Button } from "antd";
import { SearchOutlined, DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import {
  customBaseUrl,
  httpPost,
  httpPostBlob,
} from "../../store/http";
import { showLoader, hideLoader } from "../../utils/loader";
import { Box, Stack } from "@mui/material";
import { PageHeader } from "../../components/shared/page-header";
import AntDropdown from "../../components/CustomDropdown/AntDropdown";
import { SearchTableStaleState } from "../../components/shared/search-table-stale-state";
import { SearchTableEmptyState } from "../../components/shared/search-table-empty-state";
import AntTable from "../../components/CustomTable/AntTable";
import BulkBusinessModal from "./shared/components/bulk-business-upload-modal";
import BulkUploadButton from "./shared/components/bulk-upload-btn";

const { Option } = AntSelect;

const BUSINESS_TYPES = [
  { value: "CAC_BUSINESS", label: "Registered Corporate" },
  // { value: "NON_CAC_BUSINESS", label: "Non-Registered Corporate" },
];

const userStatuses = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "CLOSED", label: "Closed" },
];

const kycStatuses = [
  { value: "VERIFIED", label: "Verified" },
  { value: "PENDING", label: "Pending" },
  { value: "REJECTED", label: "Rejected" },
  { value: "UNVERIFIED", label: "Unverified" },
];

const searchTypes = [
  { value: "name", label: "Name", placeholder: "Enter name" },
  { value: "email", label: "Email", placeholder: "Enter email address" },
  { value: "phoneNumber", label: "Phone Number", placeholder: "Enter phone number" },
  { value: "tax", label: "Tax ID", placeholder: "Enter tax ID" },
  { value: "owner", label: "Owner", placeholder: "Enter owner" },
];

const CorporateManagementScreen = () => {
  const location = useLocation();

  const [businessType, setBusinessType] = useState("CAC_BUSINESS"); // dropdown replaces tabs
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [status, setStatus] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [listings, setListings] = useState([]);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);

  const currentType = searchTypes.find((t) => t.value === searchType);
  const placeholder = currentType?.placeholder || "Enter search query";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "create") {
      // reserved for future use
    }
  }, [location.search]);

  const handleSearch = useCallback(async (page = 0, size = pagination.pageSize) => {
    setLoading(true);
    showLoader();

    let finalValue = searchValue;
    if (searchType === "phoneNumber" && searchValue) {
      const cleaned = searchValue.replace(/\D/g, "");
      finalValue = `+234${cleaned.slice(-10)}`;
    }

    const payload = {
      userType: "CORPORATE",
      businessType,
      page,
      size,
      ...(finalValue && { [searchType]: finalValue }),
    };

    if (dateRange) {
      payload.createdDateFrom = dateRange[0].format('YYYY-MM-DDT00:00:00');
      payload.createdDateTo = dateRange[1].format('YYYY-MM-DDT23:59:59');
    }
    if (status) payload.status = status;
    if (kycStatus) payload.kycStatus = kycStatus;

    try {
      const response = await httpPost(
        `${customBaseUrl.authUrl}/admin/users/search`,
        payload,
      );
      if (response?.success) {
        const data = response.data;
        const usersList = data.users || (Array.isArray(data) ? data : []);
        setListings(usersList);
        setPagination(prev => ({
          ...prev,
          total: data.totalElements || usersList.length,
          current: page + 1
        }));
        setHasSearched(true);
      } else {
        NotificationManager.error(response?.message || "Failed to fetch corporate users");
      }
    } catch (error) {
      console.error("Error fetching corporate users:", error);
      NotificationManager.error("An error occurred while searching");
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, [businessType, searchType, searchValue, dateRange, status, kycStatus, pagination.pageSize]);

  const handleReset = () => {
    setSearchValue("");
    setSearchType("name");
    setBusinessType("CAC_BUSINESS");
    setStatus(null);
    setKycStatus(null);
    setDateRange(null);
    setListings([]);
    setPagination({ current: 1, pageSize: 20, total: 0 });
    setHasSearched(false);
  };

  const handleGetCorporateListing = async ({ current, pageSize }) => {
    setPagination(prev => ({ ...prev, current, pageSize }));
    await handleSearch(current - 1, pageSize);
  };

  const handleExport = async () => {
    showLoader();
    let finalValue = searchValue;
    if (searchType === "phoneNumber" && searchValue) {
      const cleaned = searchValue.replace(/\D/g, "");
      finalValue = `+234${cleaned.slice(-10)}`;
    }
    try {
      const payload = {
        userType: "CORPORATE",
        businessType,
        ...(finalValue && { [searchType]: finalValue }),
        status,
        kycStatus,
      };
      if (dateRange) {
        payload.createdDateFrom = dateRange[0].format('YYYY-MM-DDT00:00:00');
        payload.createdDateTo = dateRange[1].format('YYYY-MM-DDT23:59:59');
      }
      const res = await httpPostBlob(`${customBaseUrl.authUrl}/admin/users/export`, payload);
      if (res) {
        const url = window.URL.createObjectURL(new Blob([res], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Corporate_Export_${moment().format('YYYYMMDD_HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        NotificationManager.success("Export successful!");
      } else {
        NotificationManager.error("Export failed");
      }
    } catch (e) {
      NotificationManager.error("An error occurred during export");
    } finally {
      hideLoader();
    }
  };

  const tableColumns = [
    {
      title: "Business Name",
      key: "fullName",
      render: (record) => (
        <span style={{ fontWeight: 600, color: '#1a1a1a', textTransform: 'capitalize' }}>
          {record.fullName || `${record.firstName || ''} ${record.lastName || ''}`.trim() || '—'}
        </span>
      ),
      width: 70,
      ellipsis: true,
    },
    { title: "Email", dataIndex: "email", key: "email", width: 70, ellipsis: true },
    { title: "Phone Number", dataIndex: "phoneNumber", key: "phoneNumber", width: 55, ellipsis: true },
    {
      title: "Business Type",
      dataIndex: "businessType",
      key: "businessType",
      render: (val) => (
        <Tag color="purple" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>
          {val || '—'}
        </Tag>
      ),
      width: 55,
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag style={{
          color: isActive ? '#52c41a' : '#ff4d4f',
          background: isActive ? '#f6ffed' : '#fff2f0',
          border: `1px solid ${isActive ? '#b7eb8f' : '#ffa39e'}`,
          borderRadius: 20,
          fontWeight: 600,
          fontSize: 11,
          padding: '2px 10px',
        }}>
          {isActive ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
      width: 30,
      ellipsis: true,
    },
    {
      title: "Joined",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : '—',
      width: 50,
      ellipsis: true,
    },
    {
      title: "Action",
      key: "actions",
      render: (_, record) => {
        const id = record.id || record.userId;
        const options = ["View and Manage", "Unlock Account"];
        if (record.isActive) options.push("Deactivate");
        else options.push("Activate");
        return (
          <AntDropdown
            dropDownOptions={options}
            handleDropdownAction={(f) => f}
            userId={id}
            isArrow
          />
        );
      },
      width: 25,
      ellipsis: true,
    },
  ];

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <PageHeader
            title="Manage Corporate Customers"
            description="Oversee and manage enterprise-level waya users"
          />
          <BulkUploadButton onClick={() => setBulkUploadModal(true)} />
        </Stack>

        {/* Filter Form Card */}
        <Card
          style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>

            {/* Corporate Type (replaces tabs) */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Corporate Type</label>
              <AntSelect
                value={businessType}
                onChange={setBusinessType}
                style={{ width: '100%', height: '38px' }}
              >
                {BUSINESS_TYPES.map(t => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </AntSelect>
            </div>

            {/* Search By */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Search By</label>
              <AntSelect
                value={searchType}
                onChange={setSearchType}
                style={{ width: '100%', height: '38px' }}
              >
                {searchTypes.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </AntSelect>
            </div>

            {/* Search Value */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Search Value</label>
              <Input
                placeholder={placeholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={() => handleSearch(0)}
                style={{ height: '38px' }}
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              />
            </div>

            {/* Account Status */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Account Status</label>
              <AntSelect
                value={status}
                onChange={setStatus}
                placeholder="All Statuses"
                allowClear
                style={{ width: '100%', height: '38px' }}
              >
                {userStatuses.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
              </AntSelect>
            </div>

            {/* KYC Status */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>KYC Status</label>
              <AntSelect
                value={kycStatus}
                onChange={setKycStatus}
                placeholder="All KYC"
                allowClear
                style={{ width: '100%', height: '38px' }}
              >
                {kycStatuses.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
              </AntSelect>
            </div>

            {/* Date Range */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Date Range</label>
              <DatePicker.RangePicker
                style={{ width: '100%', height: '38px' }}
                onChange={(dates) => setDateRange(dates)}
                value={dateRange}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
            <Button icon={<ReloadOutlined />} onClick={handleReset} style={{ height: '38px', borderRadius: '8px' }}>
              Reset
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              style={{ height: '38px', borderRadius: '8px', color: '#ff6700', borderColor: '#ff6700' }}
            >
              Export
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => handleSearch(0)}
              style={{ height: '38px', borderRadius: '8px', background: '#001529', borderColor: '#001529' }}
            >
              Search
            </Button>
          </div>
        </Card>

        {/* Results */}
        {!hasSearched ? (
          <SearchTableStaleState searchFor="corporate users" />
        ) : listings.length ? (
          <AntTable
            pagination={pagination}
            users={listings}
            loading={loading}
            handleGetNext={handleGetCorporateListing}
            columns={tableColumns}
          />
        ) : (
          <SearchTableEmptyState />
        )}

        <BulkBusinessModal
          open={bulkUploadModal}
          onClose={() => setBulkUploadModal(false)}
        />
      </Box>
    </Layout>
  );
};

export default CorporateManagementScreen;
